import classNames from 'classnames';
import { m } from 'motion/react';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';

import s from './ProgressBarLoader.module.scss';

const DURATION = 50000;
const MIN_DURATION = 20000;

interface Chunk {
  loaded: boolean;
  label?: string;
}

const ProgressBarLoader: FC<{ chunks: Chunk[]; className?: string }> = ({ chunks, className }) => {
  const [progress, setProgress] = useState(0);
  const startTimestampRef = useRef<DOMHighResTimeStamp | null>(null);
  const progressRef = useRef(0);
  const requestIdRef = useRef(0);

  const calculateTargetProgress = useCallback(() => {
    const loadedCount = chunks.filter((chunk) => chunk.loaded).length;
    return (loadedCount / chunks.length) * 100;
  }, [chunks]);

  const step = useCallback(
    (timestamp: DOMHighResTimeStamp): void => {
      if (!startTimestampRef.current) startTimestampRef.current = timestamp;
      const elapsed = timestamp - startTimestampRef.current;

      const isMinDurationActive = elapsed < MIN_DURATION;
      const fakeTargetProgress = 90;
      const realTargetProgress = calculateTargetProgress();
      const targetProgress = isMinDurationActive ? fakeTargetProgress : realTargetProgress;

      const increment = 0.1;
      const value = Math.min(
        progressRef.current + (elapsed / DURATION) * (targetProgress - progressRef.current) + increment,
        targetProgress,
      );
      setProgress(value);

      if (elapsed < DURATION && value < 100) {
        requestIdRef.current = requestAnimationFrame(step);
      }
    },
    [calculateTargetProgress],
  );

  useEffect(() => {
    if (requestIdRef.current) {
      cancelAnimationFrame(requestIdRef.current);
    }

    startTimestampRef.current = null;
    requestIdRef.current = requestAnimationFrame(step);

    return () => {
      if (requestIdRef.current) {
        cancelAnimationFrame(requestIdRef.current);
      }
    };
  }, [step]);

  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  return (
    <m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={classNames(s.container, className)}>
      <progress value={progress} max='100' className={s.progress} />
      <m.div animate={{ opacity: [0.5, 1], transition: { duration: 0.5, repeat: Infinity, repeatType: 'mirror' } }}>
        {chunks.find((c) => !c.loaded) && <p className={s.label}>{chunks.find((c) => !c.loaded)?.label}</p>}
      </m.div>
    </m.div>
  );
};

export default ProgressBarLoader;
